/**
 * Host Bridge Factory
 *
 * Factory function and injection token for providing the appropriate
 * HostBridge implementation based on the runtime environment.
 *
 * Detection logic:
 * 1. Check if the app is running inside an iframe
 * 2. Check if the parent host matches *.service-now.com (via ancestorOrigins or document.referrer)
 * 3. Return ServiceNowHostBridge if both conditions are met, otherwise StandaloneHostBridge
 */

import { InjectionToken } from '@angular/core';
import { HostBridge } from './host-bridge.interface';
import { ServiceNowHostBridge } from './servicenow-host-bridge.service';
import { StandaloneHostBridge } from './standalone-host-bridge.service';

/**
 * Injection token for the HostBridge service.
 * Use this token to inject the appropriate bridge implementation.
 *
 * @example
 * ```typescript
 * constructor(@Inject(HOST_BRIDGE) private hostBridge: HostBridge) {}
 * // or with inject()
 * private hostBridge = inject(HOST_BRIDGE);
 * ```
 */
export const HOST_BRIDGE = new InjectionToken<HostBridge>('HOST_BRIDGE');

/**
 * Check if the app is running inside an iframe
 */
function isRunningInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
}

/**
 * Check if a hostname matches ServiceNow (*.service-now.com)
 */
function isServiceNowHostname(hostname: string): boolean {
  return hostname.endsWith('.service-now.com');
}

/**
 * Check if the parent host is a ServiceNow instance (*.service-now.com)
 * Uses ancestorOrigins (Chrome/Edge/Safari) with document.referrer as fallback (Firefox)
 */
function isServiceNowHost(): boolean {
  try {
    // First try ancestorOrigins - works in cross-origin iframes (Chrome, Edge, Safari)
    if (window.location.ancestorOrigins?.length > 0) {
      const parentOrigin = window.location.ancestorOrigins[0];
      const parentUrl = new URL(parentOrigin);
      return isServiceNowHostname(parentUrl.hostname);
    }

    // Fallback to document.referrer (Firefox, or when ancestorOrigins unavailable)
    const referrer = document.referrer;
    if (referrer) {
      const referrerUrl = new URL(referrer);
      return isServiceNowHostname(referrerUrl.hostname);
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Factory function to create the appropriate HostBridge implementation.
 *
 * Returns ServiceNowHostBridge when:
 * - The app is running inside an iframe AND
 * - The parent host matches *.service-now.com
 *
 * Returns StandaloneHostBridge otherwise.
 */
export function hostBridgeFactory(): HostBridge {
  const inIframe = isRunningInIframe();
  const isServiceNow = isServiceNowHost();

  console.log('[HostBridgeFactory] Detection:', {
    inIframe,
    isServiceNow,
    ancestorOrigins: window.location.ancestorOrigins?.[0] ?? null,
    referrer: document.referrer || null,
  });

  if (inIframe && isServiceNow) {
    console.log('[HostBridgeFactory] Creating ServiceNowHostBridge (embedded in ServiceNow)');
    return new ServiceNowHostBridge();
  } else {
    console.log('[HostBridgeFactory] Creating StandaloneHostBridge (standalone mode)');
    return new StandaloneHostBridge();
  }
}

/**
 * Provider configuration for the HostBridge.
 * Use this in your app.config.ts or module providers.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     hostBridgeProvider,
 *     // ... other providers
 *   ]
 * };
 * ```
 */
export const hostBridgeProvider = {
  provide: HOST_BRIDGE,
  useFactory: hostBridgeFactory,
};
